import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import MatchChat from '../components/MatchChat';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { matchesApi, MatchPair } from '../api/matches';
import { useAuth } from '../contexts/AuthContext';

const resultSchema = z.object({
  sets: z.array(
    z.object({
      home: z.number().min(0).max(99),
      away: z.number().min(0).max(99),
    })
  ).min(1, 'Mindestens ein Satz'),
});

type ResultForm = z.infer<typeof resultSchema>;

function PlayerName({ player }: { player?: { id: string; first_name: string; last_name: string; level?: string; photo_url?: string } | null }) {
  const API_URL = process.env.REACT_APP_API_URL ?? '';
  if (!player) return <span className="text-gray-400 italic text-lg">Noch offen</span>;
  return (
    <Link to={`/users/${player.id}`} className="hover:text-primary-600">
      <div className="flex flex-col items-center gap-2">
        {player.photo_url ? (
          <img
            src={`${API_URL}${player.photo_url}`}
            alt={`${player.first_name} ${player.last_name}`}
            className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xl font-bold border-2 border-gray-200">
            {player.first_name[0]}{player.last_name[0]}
          </div>
        )}
        <div className="text-center">
          <p className="font-semibold text-gray-900">{player.first_name} {player.last_name}</p>
          {player.level && <p className="text-sm text-gray-500">{player.level}</p>}
        </div>
      </div>
    </Link>
  );
}

function PairDisplay({ pair }: { pair?: MatchPair | null }) {
  if (!pair) return <span className="text-gray-400 italic text-lg">Noch offen</span>;
  const p1 = pair.player1;
  const p2 = pair.player2;
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="w-16 h-16 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xl font-bold border-2 border-gray-200">
        {p1 ? p1.first_name[0] : '?'}{p2 ? p2.first_name[0] : '?'}
      </div>
      <div className="text-center">
        <p className="font-semibold text-gray-900 text-sm">
          {p1 ? <Link to={`/users/${p1.id}`} className="hover:text-primary-600">{p1.first_name} {p1.last_name}</Link> : '?'}
        </p>
        <p className="text-xs text-gray-400">& {p2 ? <Link to={`/users/${p2.id}`} className="hover:text-primary-600">{p2.first_name} {p2.last_name}</Link> : '?'}</p>
      </div>
    </div>
  );
}

const PHASE_LABEL: Record<string, string> = {
  group: 'Gruppe',
  round_of_16: 'Achtelfinale',
  quarterfinal: 'Viertelfinale',
  semifinal: 'Halbfinale',
  third_place: 'Spiel um Platz 3',
  final: 'Finale',
};

export default function MatchDetail() {
  const { id } = useParams<{ id: string }>();
  const matchId = parseInt(id!);
  const { user, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState('');
  const [rejectNote, setRejectNote] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showAdminForm, setShowAdminForm] = useState(false);

  const { data: match, isLoading } = useQuery({
    queryKey: ['match', matchId],
    queryFn: () => matchesApi.get(matchId).then((r) => r.data),
  });

  const { register, control, handleSubmit, formState: { errors } } = useForm<ResultForm>({
    resolver: zodResolver(resultSchema),
    defaultValues: { sets: [{ home: 0, away: 0 }, { home: 0, away: 0 }, { home: 0, away: 0 }] },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'sets' });

  const submitMutation = useMutation({
    mutationFn: (data: ResultForm) => matchesApi.submitResult(matchId, data.sets),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['match', matchId] });
      setActionError('');
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setActionError(e.response?.data?.detail ?? 'Fehler');
    },
  });

  const confirmMutation = useMutation({
    mutationFn: () => matchesApi.confirm(matchId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['match', matchId] }),
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setActionError(e.response?.data?.detail ?? 'Fehler');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => matchesApi.reject(matchId, rejectNote),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['match', matchId] });
      setShowRejectForm(false);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setActionError(e.response?.data?.detail ?? 'Fehler');
    },
  });

  const adminEditMutation = useMutation({
    mutationFn: (data: ResultForm) => matchesApi.adminEdit(matchId, data.sets),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['match', matchId] });
      setShowAdminForm(false);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setActionError(e.response?.data?.detail ?? 'Fehler');
    },
  });

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-48 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  if (!match) {
    return <div className="text-center py-16 text-gray-500">Spiel nicht gefunden</div>;
  }

  const uid = user?.id;
  const isPairMatch = !!(match.home_pair_id || match.away_pair_id);
  let isParticipant = false;
  if (uid) {
    if (!isPairMatch) {
      isParticipant = uid === match.home_player_id || uid === match.away_player_id;
    } else {
      const inHome = match.home_pair && (uid === match.home_pair.player1?.id || uid === match.home_pair.player2?.id);
      const inAway = match.away_pair && (uid === match.away_pair.player1?.id || uid === match.away_pair.player2?.id);
      isParticipant = !!(inHome || inAway);
    }
  }
  const canSubmit = isParticipant && match.status === 'pending' && !match.is_bye;
  const canConfirm = isParticipant && match.status === 'result_submitted' &&
    match.result?.submitter?.id !== uid;
  const canReject = canConfirm;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Link to={`/leagues/${match.league_id}`} className="text-sm text-primary-600 hover:text-primary-700">
          ← Zur Liga
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-2">
          {PHASE_LABEL[match.phase] ?? match.phase}
        </h1>
      </div>

      {/* Match score card */}
      <div className="card mb-6">
        <div className="flex flex-col sm:flex-row items-center justify-around py-4 gap-4">
          {isPairMatch ? <PairDisplay pair={match.home_pair} /> : <PlayerName player={match.home_player} />}
          <div className="text-center px-2 order-first sm:order-none">
            {match.result && match.result.status === 'confirmed' ? (
              <>
                <div className="text-4xl font-bold text-gray-900">
                  {match.result.home_sets_won} : {match.result.away_sets_won}
                </div>
                <p className="text-sm text-gray-400 mt-1">Sätze</p>
                {match.result.sets && Array.isArray(match.result.sets) && (
                  <div className="flex gap-2 mt-2 justify-center">
                    {(match.result.sets as { home: number; away: number }[]).map((s, i) => (
                      <span key={i} className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {s.home}:{s.away}
                      </span>
                    ))}
                  </div>
                )}
              </>
            ) : match.result && match.result.status === 'pending' ? (
              <div className="text-center">
                <span className="badge bg-yellow-100 text-yellow-700 text-sm">Warte auf Bestätigung</span>
                {match.result.sets && Array.isArray(match.result.sets) && (
                  <div className="flex gap-2 mt-2 justify-center">
                    {(match.result.sets as { home: number; away: number }[]).map((s, i) => (
                      <span key={i} className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {s.home}:{s.away}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <span className="text-gray-300 text-2xl font-bold">vs</span>
            )}
            <div className="mt-3">
              <span className={`badge ${
                match.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                match.status === 'result_submitted' ? 'bg-yellow-100 text-yellow-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {match.status === 'pending' ? 'Offen' :
                 match.status === 'result_submitted' ? 'Ergebnis eingereicht' :
                 match.status === 'confirmed' ? 'Bestätigt' : match.status}
              </span>
            </div>
          </div>
          {isPairMatch ? <PairDisplay pair={match.away_pair} /> : <PlayerName player={match.away_player} />}
        </div>

        {match.status === 'confirmed' && (
          <div className="border-t border-gray-100 pt-3 text-center space-y-1">
            <span className="text-sm font-medium text-primary-600">
              Sieger:{' '}
              {isPairMatch && match.winner_pair
                ? `${match.winner_pair.player1?.first_name ?? '?'} ${match.winner_pair.player1?.last_name ?? ''} / ${match.winner_pair.player2?.first_name ?? '?'} ${match.winner_pair.player2?.last_name ?? ''}`
                : match.winner
                  ? `${match.winner.first_name} ${match.winner.last_name}`
                  : '–'}
            </span>
            {match.result?.admin_edited_by && (
              <div className="flex items-center justify-center gap-1.5 text-xs text-amber-600">
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span>
                  Nachträglich geändert
                  {match.result.admin_editor && (
                    <> von {match.result.admin_editor.first_name} {match.result.admin_editor.last_name}</>
                  )}
                  {match.result.admin_edited_at && (
                    <> · {new Date(match.result.admin_edited_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</>
                  )}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {actionError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-4">
          {actionError}
        </div>
      )}

      {/* Submit result form */}
      {canSubmit && (
        <div className="card mb-4">
          <h3 className="font-semibold text-gray-900 mb-4">Ergebnis eintragen</h3>
          <form onSubmit={handleSubmit((data) => submitMutation.mutate(data))}>
            <div className="space-y-2 mb-4">
              {fields.map((field, index) => (
                <div key={field.id} className="flex items-center gap-3">
                  <span className="text-sm text-gray-500 w-14">Satz {index + 1}</span>
                  <input
                    type="number"
                    min="0"
                    max="99"
                    {...register(`sets.${index}.home`, { valueAsNumber: true })}
                    className="input w-20 text-center"
                  />
                  <span className="text-gray-400 font-bold">:</span>
                  <input
                    type="number"
                    min="0"
                    max="99"
                    {...register(`sets.${index}.away`, { valueAsNumber: true })}
                    className="input w-20 text-center"
                  />
                  {fields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="text-red-400 hover:text-red-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => append({ home: 0, away: 0 })}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                + Satz hinzufügen
              </button>
              <button type="submit" disabled={submitMutation.isPending} className="btn btn-primary">
                {submitMutation.isPending ? 'Speichern...' : 'Ergebnis einreichen'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Confirm / reject */}
      {(canConfirm || canReject) && (
        <div className="card mb-4">
          <h3 className="font-semibold text-gray-900 mb-2">Ergebnis bestätigen</h3>
          <p className="text-sm text-gray-600 mb-4">
            {match.result?.submitter?.first_name} {match.result?.submitter?.last_name} hat ein Ergebnis eingetragen.
            Bitte überprüfe es und bestätige oder lehne es ab.
          </p>
          <div className="flex gap-3">
            {canConfirm && (
              <button
                onClick={() => confirmMutation.mutate()}
                disabled={confirmMutation.isPending}
                className="btn btn-primary"
              >
                Bestätigen
              </button>
            )}
            {canReject && !showRejectForm && (
              <button
                onClick={() => setShowRejectForm(true)}
                className="btn btn-danger"
              >
                Ablehnen
              </button>
            )}
          </div>

          {showRejectForm && (
            <div className="mt-4">
              <label className="label">Grund für Ablehnung</label>
              <textarea
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                className="input"
                rows={3}
                placeholder="Bitte beschreibe, warum das Ergebnis falsch ist..."
              />
              <div className="flex gap-3 mt-2">
                <button
                  onClick={() => rejectMutation.mutate()}
                  disabled={rejectMutation.isPending || !rejectNote.trim()}
                  className="btn btn-danger btn-sm"
                >
                  Ablehnen bestätigen
                </button>
                <button onClick={() => setShowRejectForm(false)} className="btn btn-secondary btn-sm">
                  Abbrechen
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Admin section */}
      {isAdmin && !match.is_bye && (
        <div className="card border-orange-200 bg-orange-50">
          <h3 className="font-semibold text-orange-800 mb-2">Admin: Ergebnis korrigieren</h3>
          {match.result?.admin_edited_by && !showAdminForm && (
            <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-100 border border-amber-200 rounded px-2 py-1.5 mb-3">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>
                Zuletzt geändert von {match.result.admin_editor?.first_name} {match.result.admin_editor?.last_name}
                {match.result.admin_edited_at && (
                  <> · {new Date(match.result.admin_edited_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</>
                )}
              </span>
            </div>
          )}
          {!showAdminForm ? (
            <button
              onClick={() => setShowAdminForm(true)}
              className="btn btn-sm bg-orange-600 text-white hover:bg-orange-700"
            >
              {match.result?.admin_edited_by ? 'Erneut korrigieren' : 'Ergebnis überschreiben'}
            </button>
          ) : (
            <form onSubmit={handleSubmit((data) => adminEditMutation.mutate(data))}>
              <div className="space-y-2 mb-4">
                {fields.map((field, index) => (
                  <div key={field.id} className="flex items-center gap-3">
                    <span className="text-sm text-gray-500 w-14">Satz {index + 1}</span>
                    <input
                      type="number"
                      min="0"
                      {...register(`sets.${index}.home`, { valueAsNumber: true })}
                      className="input w-20 text-center"
                    />
                    <span className="text-gray-400 font-bold">:</span>
                    <input
                      type="number"
                      min="0"
                      {...register(`sets.${index}.away`, { valueAsNumber: true })}
                      className="input w-20 text-center"
                    />
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={adminEditMutation.isPending} className="btn btn-sm bg-orange-600 text-white hover:bg-orange-700">
                  Speichern
                </button>
                <button type="button" onClick={() => setShowAdminForm(false)} className="btn btn-secondary btn-sm">
                  Abbrechen
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Match Chat */}
      {!match.is_bye && (isParticipant || isAdmin) && (
        <MatchChat matchId={matchId} />
      )}
    </div>
  );
}
